<template>
  <div
    class="DurationRangeInput"
    :class="{
      'DurationRangeInput--shouldSlide': shouldSlide,
    }"
    :data-direction="direction"
  >
    <div
      class="DurationRangeInput__header"
      data-testid="header"
      aria-hidden="true"
    >
      <Transition>
        <span
          :key="String(!!modelValue)"
          class="DurationRangeInput__headerTitle"
        >{{ formattedHeader }}</span>
      </Transition>
    </div>

    <div class="DurationRangeInput__trackContainer">
      <div class="DurationRangeInput__track">
        <div
          v-for="(_tick, i) in ticks"
          :key="i"
          class="DurationRangeInput__trackTick"
          :data-size="Math.round(i / 4) === i / 4 ? 'large' : 'small'"
          :style="{
            left: `${i / ticks.length * 75}%`,
          }"
        />
      </div>

      <Basketball class="DurationRangeInput__thumb" />
    </div>

    <div class="DurationRangeInput__tickLabelContainer">
      <div
        class="DurationRangeInput__tickLabel"
        data-testid="tickLabel"
      >
        <abbr title="1 hour">1h</abbr>
      </div>
      <div
        class="DurationRangeInput__tickLabel"
        data-testid="tickLabel"
      >
        <abbr title="2 hours">2h</abbr>
      </div>
      <div
        class="DurationRangeInput__tickLabel"
        data-testid="tickLabel"
      >
        <abbr title="3 hours">3h</abbr>
      </div>
      <Icon
        class="DurationRangeInput__tickLabel--icon DurationRangeInput__tickLabel"
        data-testid="tickLabel"
        name="infinity"
      />
    </div>

    <input
      v-model.number="inputModelValue"
      class="DurationRangeInput__input"
      type="range"
      min="1"
      max="4"
      step="0.1"
      :aria-valuetext="formattedHeader"
    >
  </div>
</template>

<script lang="ts" setup>
import Basketball from "@/components/Basketball.vue";
import Icon from "@/components/Icon.vue";
import isTruthy from "@/lib/utils/boolean/isTruthy";
import haptic from "@/lib/utils/platform/haptic";
import { ImpactStyle } from "@capacitor/haptics";
import { addHours, format } from "date-fns";
import { computed, ref, watch } from "vue";

const modelValue = defineModel<number | null>({
  required: true,
});

const {
  startDate,
} = defineProps<{
  startDate?: Date,
}>();

const inputModelValue = computed({
  get () {
    if (modelValue.value === null) return 4;

    return modelValue.value;
  },
  set (value: number | null) {
    modelValue.value = clampValue(value);
  },
});

const hourMin = 1;
const hourMax = 3;
const valueMin = hourMin;
const valueMax = hourMax + 1;

const ticks = ref(Array.from({
  length: 3 * hourMax,
}));

const direction = ref<"left" | "right">("left");
const shouldSlide = ref(false);

watch(modelValue, (newValue, oldValue) => {
  direction.value = newValue === null || oldValue && newValue > oldValue ? "right" : "left";
  shouldSlide.value = newValue === null || oldValue === null || Math.abs(newValue - oldValue) >= 0.5;

  haptic(newValue === null || oldValue === null ? ImpactStyle.Heavy : ImpactStyle.Medium);
});

const formattedHeader = computed(() => {
  if (modelValue.value === null) return "Until everyone’s tired";

  const formattedEndTime = startDate
    ? `until ${format(addHours(startDate, modelValue.value), "h:mm a")}`
    : null;

  return [formattedDuration.value, formattedEndTime].filter(isTruthy).join(", ");
});

const hours = computed(() => {
  if (!modelValue.value) return 0;

  return Math.floor(modelValue.value);
});

const minutes = computed(() => {
  if (!modelValue.value) return 0;

  return (modelValue.value - hours.value) * 60;
});

const thumbPosition = computed(() => {
  if (modelValue.value === null) return valueMax - valueMin;

  return modelValue.value - valueMin;
});

const formattedDuration = computed(() => {
  const formattedHours = [hours.value, minuteFraction.value].filter(isTruthy).join(" ");

  return [formattedHours, modelValue.value !== 1 ? "hours" : "hour"].join(" ");
});

const minuteFraction = computed(() => {
  switch (minutes.value) {
    case 15:
      return "¼";
    case 30:
      return "½";
    case 45:
      return "¾";
  }

  return "";
});

function clampValue (value: number | null) {
  if (value && value <= hourMax + (valueMax - hourMax) / 2) return Math.round(Math.min(hourMax, value) * valueMax) / valueMax;

  return null;
}
</script>

<style lang="scss" scoped>
.DurationRangeInput {
  --thumbRadius: 16px;
  --thumbSize: calc(2 * var(--thumbRadius));
  --inputThumbSize: calc(2 * (16px + var(--thumbRadius)));
  --inputHeight: 122px;
  position: relative;

  display: grid;
  padding: 20px 16px 16px;

  border: 2px solid var(--themeColorNeutral900);
  border-radius: 6px;
  background-color: var(--themeColorInputBackground);
  box-shadow: 0 0 0 0 transparent;

  transition: 350ms;
  transition-property: box-shadow, border-color, background-color;

  &:has(:disabled) {
    border-color: var(--themeColorNeutral400);
    background-color: var(--themeColorNeutral0);
    color: var(--themeDarkTransparent500);
  }

  &:focus-within {
    box-shadow: 0 0 0 3px var(--themeColorSecondary);
  }
}

.DurationRangeInput__header {
  position: relative;

  display: grid;
  place-items: center;
  margin-bottom: 20px;

  font: var(--fontWeightNormal) 18px/1 var(--fontFamilyMono);
}

.DurationRangeInput__headerTitle {
  display: block;
  transition: 150ms;
  transition-property: opacity, transform;

  &.v-leave-active {
    position: absolute;
  }

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }

  .DurationRangeInput[data-direction='right'] &.v-enter-from,
  .DurationRangeInput[data-direction='left'] &.v-leave-to {
    transform: translateX(-24px);
  }

  .DurationRangeInput[data-direction='right'] &.v-leave-to,
  .DurationRangeInput[data-direction='left'] &.v-enter-from {
    transform: translateX(24px);
  }
}

.DurationRangeInput__tickLabel {
  font: var(--fontWeightNormal) 16px/1 var(--fontFamilyMono);
}

.DurationRangeInput__tickLabel--icon {
  width: 16px;
  height: 16px;
}

.DurationRangeInput__input {
  position: absolute;
  inset: 0;

  outline: none;
  background: none;
  appearance: none;

  &::-webkit-slider-runnable-track {
    display: grid;
    align-items: center;
    height: var(--inputHeight);
  }

  &::-moz-range-track {
    display: grid;
    align-items: center;
    height: var(--inputHeight);
  }

  &::-webkit-slider-thumb {
    width: var(--inputThumbSize);
    height: var(--inputHeight);

    background-color: transparent;
    box-shadow: none;
    appearance: none;
  }

  &::-moz-range-thumb {
    width: var(--inputThumbSize);
    height: var(--inputHeight);

    border: none;
    border-radius: 0;
    background-color: transparent;
  }

  &:not(:disabled) {
    cursor: pointer;
  }
}

.DurationRangeInput__trackContainer {
  position: relative;

  display: grid;
  align-items: center;
  height: 24px;

  pointer-events: none;
}

.DurationRangeInput__track {
  position: relative;

  height: 6px;
  margin: 0 var(--thumbRadius);

  background-color: var(--themeColorNeutral150);

  &::after {
    --size: 18px;
    position: absolute;
    top: calc(50% - var(--size) / 2);
    left: calc(100% - var(--size) / 2);

    width: var(--size);
    height: var(--size);

    border-radius: 50%;
    background-color: inherit;

    content: "";
  }
}

.DurationRangeInput__trackTick {
  position: absolute;
  top: 50%;
  background-color: inherit;

  &[data-size="small"] {
    transform: translate(-1px, -50%);
    width: 2px;
    height: 16px;
  }

  &[data-size="large"] {
    transform: translate(-2px, -50%);
    width: 3px;
    height: 24px;
  }
}

.DurationRangeInput__thumb {
  position: absolute;
  top: calc(50% - var(--thumbRadius));
  left: calc((100% - var(--thumbSize)) * v-bind(thumbPosition) / v-bind(hourMax));

  .DurationRangeInput--shouldSlide & {
    transition: 150ms left;
  }

  .DurationRangeInput:has(:disabled) & {
    --iconColor: var(--themeColorNeutral150);
    --strokeColor: var(--themeColorNeutral500);
  }
}

.DurationRangeInput__tickLabelContainer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding: 0 8px;
}
</style>
